/**
 * Standardized Error Handling for Wizard Hooks
 *
 * Provides consistent error handling across all wizard-related hooks.
 * Ensures proper logging in development and user-friendly notifications in production.
 */

import { useCallback } from 'react';
import { useNotifications } from '../../../../hooks/useNotifications';

/**
 * Custom hook for standardized wizard error handling
 *
 * Usage:
 * ```ts
 * const handleError = useWizardErrorHandler('useRoadDetection');
 *
 * try {
 *   await someOperation();
 * } catch (error) {
 *   handleError(error, 'GPS detection', { coordinates });
 * }
 * ```
 */
export function useWizardErrorHandler(hookName: string) {
  const { showError } = useNotifications();
  const isDev = import.meta.env.DEV;

  // ✅ Memoize returned function to prevent infinite re-render loops
  // Now stable with memoized showError from useNotifications
  return useCallback(
    (
      error: unknown,
      operation: string,
      details?: Record<string, unknown>,
      userMessage?: string
    ) => {
      const errorMessage = error instanceof Error ? error.message : String(error);

      // Development: Detailed console logging
      if (isDev) {
        console.error(`[${hookName}] Error in ${operation}:`, {
          error: errorMessage,
          stack: error instanceof Error ? error.stack : undefined,
          details,
        });
      }

      // Show user notification with fallback message
      const notificationMessage =
        userMessage || `An error occurred during ${operation}. Please try again.`;
      showError(notificationMessage);
    },
    [hookName, showError, isDev]
  );
}
